import { getCueingBlocks } from '@/api/queries/cockpit';
import { getProcedureQueryOptions } from '@/api/queries/procedures';
import { CueReactivityCard } from '@/components/cockpit/cueReactivity/cueReactivityCard';
import { BlocksTimeline } from '@/components/cockpit/procedureBlocks/blocksTimeline';
import { ProcedureBlock } from '@/components/cockpit/procedureBlocks/procedureBlock';
import { ErrorComponent } from '@/components/generalError';
import { CockpitContext } from '@/context/cockpitContext';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useContext, useEffect } from 'react';
import { isProcedureCompleted } from '@/lib/procedureStatus';
import { assembleRoute } from '@/types/routes';

export const Route = createFileRoute('/_plain/cockpit/$procedureId/treatment')({
  component: RouteComponent,
  errorComponent: ErrorComponent,
});

const POLLING_INTERVAL = 1000; // refetch every 1 second

function RouteComponent() {
  const { procedureId } = Route.useParams();
  const navigate = useNavigate();

  const { setNewCueingBlocks } = useContext(CockpitContext);

  const { data: procedure } = useSuspenseQuery(
    getProcedureQueryOptions(procedureId),
  );
  // In the future we might switch to websockets for better performance
  const { data: cueingBlocks, isLoading } = useQuery({
    ...getCueingBlocks(procedureId),
    refetchInterval: POLLING_INTERVAL,
  });

  // Redirect to overview if checklist is not accepted
  if (!procedure.isCockpitChecklistAccepted) {
    void navigate({ to: assembleRoute('cockpitOverview', { procedureId }), replace: true });
    return null;
  }

  // Redirect to standard procedure details if already completed
  if (isProcedureCompleted(procedure)) {
    void navigate({ to: assembleRoute('procedureDetails', { procedureId }), replace: true });
    return null;
  }

  useEffect(() => {
    if (isLoading || !cueingBlocks?.items.length) return;

    setNewCueingBlocks(cueingBlocks.items);
  }, [ cueingBlocks, setNewCueingBlocks, isLoading ]);

  return (
    <div className="grid grid-cols-2 gap-space-lg flex-1 min-h-0 overflow-hidden">
      <div className="flex flex-col">
        <CueReactivityCard
          procedureId={ procedureId }
          subjectHumanReadableId={ procedure.subject.humanReadableId }
        />
      </div>
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden gap-space-lg">
        <BlocksTimeline />
        <ProcedureBlock />
      </div>
    </div>
  );
}
